/**
 * IM:SPLREP, the smoothing spline of EAV.RDP.INTERP (see interpMethods.ts,
 * which holds the method table): a cubic smoothing spline with smoothing
 * factor s = m - sqrt(2m) (m the number of points, all weights 1), fitted by
 * Dierckx's algorithm (FITPACK `curfit`, P. Dierckx, "Curve and Surface
 * Fitting with Splines", 1993) and evaluated as `splev` does, its end
 * polynomials continued outside the range. This is our own transcription of
 * the published algorithm, step for step in its arithmetic order, which is
 * what gives LSEG's last digits: exact on d1, d2b, d4, d6-d9, p4, d20, d21.
 *
 * Proven paths: the least-squares cubic when it is smooth enough (no interior
 * knot: d2b, d4, d6-d9, p4); one knot from fpknot, then the search for the
 * smoothing parameter p (d1: knot at 7, 8 iterations); FITPACK's
 * interpolation knots at n = nmax, then the search for p (d20, d21). Two or
 * more knots from fpknot (d18, d19) give the right knots and iterations but
 * miss LSEG by up to 9e-15, and a search for p that does not converge is not
 * captured: those curves are left out (splrepMatched).
 */

/** The cubic. */
const K = 3;
const K1 = K + 1;
const K2 = K + 2;
const NMIN = 2 * K1;
/** curfit's tolerance on |f(p) - s|, relative to s, and its maximum number of iterations. */
const TOL = 0.001;
const MAXIT = 20;

/** A fitted spline: the knots t[1..n] and the coefficients c[1..n-K1] (index 0 unused, as FITPACK's 1-based arrays). */
type Spline = { t: number[]; n: number; c: number[] };

/** The K+1 B-splines that are not 0 at x, with t[l] <= x < t[l+1] (FITPACK fpbspl: de Boor's recurrence). */
function bsplines(t: number[], x: number, l: number): number[] {
  const h = new Array<number>(K2).fill(0);
  const hh = new Array<number>(K2).fill(0);
  h[1] = 1;
  for (let j = 1; j <= K; j++) {
    for (let i = 1; i <= j; i++) hh[i] = h[i];
    h[1] = 0;
    for (let i = 1; i <= j; i++) {
      const li = l + i;
      const lj = li - j;
      const f = hh[i] / (t[li] - t[lj]);
      h[i] = h[i] + f * (t[li] - x);
      h[i + 1] = f * (x - t[lj]);
    }
  }
  return h;
}

/** A Givens rotation that zeroes piv against ww: [cos, sin, the new ww] (FITPACK fpgivs). */
function givens(piv: number, ww: number): [number, number, number] {
  const store = Math.abs(piv);
  const dd = store >= ww ? store * Math.sqrt(1 + (ww / piv) * (ww / piv)) : ww * Math.sqrt(1 + (piv / ww) * (piv / ww));
  return [ww / dd, piv / dd, dd];
}

/** The rotation applied to the pair (a, b): [the new a, the new b] (FITPACK fprota). */
function rotate(cos: number, sin: number, a: number, b: number): [number, number] {
  return [cos * a - sin * b, cos * b + sin * a];
}

/** Back substitution in an upper triangular band matrix a (n rows, bandwidth k) (FITPACK fpback). */
function backSubstitute(a: number[][], z: number[], n: number, k: number): number[] {
  const c = new Array<number>(n + 1).fill(0);
  const k1 = k - 1;
  c[n] = z[n] / a[n][1];
  let i = n - 1;
  for (let j = 2; j <= n; j++) {
    let store = z[i];
    const i1 = j <= k1 ? j - 1 : k1;
    let m = i;
    for (let l = 1; l <= i1; l++) {
      m++;
      store = store - c[m] * a[i][l + 1];
    }
    c[i] = store / a[i][1];
    i--;
  }
  return c;
}

/** The jumps of the K-th derivative of the B-splines at the interior knots, scaled (FITPACK fpdisc). */
function discontinuities(t: number[], n: number): number[][] {
  const nk1 = n - K1;
  const fac = (nk1 - K) / (t[nk1 + 1] - t[K1]);
  const b: number[][] = [];
  for (let l = K2; l <= nk1; l++) {
    const lmk = l - K1;
    const h = new Array<number>(2 * K2 + 1).fill(0);
    for (let j = 1; j <= K1; j++) {
      h[j] = t[l] - t[l + j - K2];
      h[j + K1] = t[l] - t[l + j];
    }
    b[lmk] = new Array<number>(K2 + 1).fill(0);
    let lp = lmk;
    for (let j = 1; j <= K2; j++) {
      let jk = j;
      let prod = h[j];
      for (let i = 1; i <= K; i++) {
        jk++;
        prod = prod * h[jk] * fac;
      }
      b[lmk][j] = (t[lp + K1] - t[lp]) / prod;
      lp++;
    }
  }
  return b;
}

/**
 * The least-squares spline on the knots t[1..n]: the observation matrix
 * reduced to triangle row by row by Givens rotations. Returns the triangle a,
 * the rotated right-hand side z, the sum of squared residuals fp and the
 * B-spline values q at each point.
 */
function leastSquares(x: number[], y: number[], t: number[], n: number) {
  const m = x.length - 1;
  const nk1 = n - K1;
  const z = new Array<number>(nk1 + 1).fill(0);
  const a = Array.from({ length: nk1 + 1 }, () => new Array<number>(K2 + 1).fill(0));
  const q: number[][] = [];
  let fp = 0;
  let l = K1;
  for (let it = 1; it <= m; it++) {
    const xi = x[it];
    let yi = y[it];
    while (!(xi < t[l + 1] || l === nk1)) l++;
    const h = bsplines(t, xi, l);
    q[it] = h.slice();
    let j = l - K1;
    for (let i = 1; i <= K1; i++) {
      j++;
      const piv = h[i];
      if (piv === 0) continue;
      const [cos, sin, dd] = givens(piv, a[j][1]);
      a[j][1] = dd;
      [yi, z[j]] = rotate(cos, sin, yi, z[j]);
      if (i === K1) break;
      let i2 = 1;
      for (let i1 = i + 1; i1 <= K1; i1++) {
        i2++;
        [h[i1], a[j][i2]] = rotate(cos, sin, h[i1], a[j][i2]);
      }
    }
    fp = fp + yi * yi;
  }
  return { a, z, fp, q };
}

/**
 * The sum of squared residuals fp of the spline at the data points: the
 * spline's value there (from the coefficients c and the B-spline values q)
 * minus y, squared and summed.
 */
function sumOfSquares(x: number[], y: number[], t: number[], n: number, c: number[], q: number[][]): number {
  const m = x.length - 1;
  const nk1 = n - K1;
  let fp = 0;
  let l = K2;
  for (let it = 1; it <= m; it++) {
    if (!(x[it] < t[l] || l > nk1)) l++;
    let l0 = l - K2;
    let term = 0;
    for (let j = 1; j <= K1; j++) {
      l0++;
      term = term + c[l0] * q[it][j];
    }
    fp = fp + (term - y[it]) * (term - y[it]);
  }
  return fp;
}

/** FITPACK fprati: the next p, by rational interpolation through (p1, f1), (p2, f2), (p3, f3). */
function rationalStep(p1: number, f1: number, p2: number, f2: number, p3: number, f3: number) {
  let p: number;
  if (p3 > 0) {
    const h1 = f1 * (f2 - f3);
    const h2 = f2 * (f3 - f1);
    const h3 = f3 * (f1 - f2);
    p = -(p1 * p2 * h3 + p2 * p3 * h1 + p3 * p1 * h2) / (p1 * h1 + p2 * h2 + p3 * h3);
  } else {
    p = (p1 * (f1 - f3) * f2 - p2 * (f2 - f3) * f1) / ((f1 - f2) * f3);
  }
  return f2 < 0 ? { p, p1, f1, p3: p2, f3: f2 } : { p, p1: p2, f1: f2, p3, f3 };
}

/**
 * FITPACK fpknot: a knot added in the knot interval with the largest sum of
 * squared residuals fpint (among those holding data points), at its middle
 * data point; the intervals' sums and data counts are split accordingly.
 * The arrays are 1-based and updated in place (t, fpint, nrdata); it returns
 * nothing, and the caller adds one to n and to nrint.
 */
function addKnot(x: number[], t: number[], n: number, fpint: number[], nrdata: number[], nrint: number): void {
  const k = (n - nrint - 1) / 2;
  let [fpmax, number, maxpt, maxbeg] = [0, 0, 0, 0];
  let jbegin = 1;
  for (let j = 1; j <= nrint; j++) {
    const jpoint = nrdata[j];
    if (!(fpmax >= fpint[j] || jpoint === 0)) [fpmax, number, maxpt, maxbeg] = [fpint[j], j, jpoint, jbegin];
    jbegin = jbegin + jpoint + 1;
  }
  const ihalf = Math.floor(maxpt / 2) + 1;
  const nrx = maxbeg + ihalf;
  const next = number + 1;
  for (let j = next; j <= nrint; j++) {
    const jj = next + nrint - j;
    fpint[jj + 1] = fpint[jj];
    nrdata[jj + 1] = nrdata[jj];
    t[jj + k + 1] = t[jj + k];
  }
  nrdata[number] = ihalf - 1;
  nrdata[next] = maxpt - ihalf;
  fpint[number] = (fpmax * nrdata[number]) / maxpt;
  fpint[next] = (fpmax * nrdata[next]) / maxpt;
  t[next + k] = x[nrx];
}

/**
 * The smoothing spline of (xs, ys), as FITPACK curfit computes it with
 * iopt = 0, k = 3, weights 1 and s = m - sqrt(2m); undefined on a path the
 * goldens do not prove.
 * Knots: none at first (the least-squares cubic, accepted when its sum of
 * squared residuals fp0 is within s); then fpknot adds knots, nplus at a
 * time, until fp < s; if n reaches nmax = m + 4 the knots are put at
 * x_3 .. x_{m-2} (FITPACK's interpolation branch). Then p is searched.
 * Proven (goldens d1, d20, d21): the final knots are none, the single knot
 * fpknot adds first, or the interpolation knots. Not proven, hence undefined:
 * two or more knots from fpknot (goldens d18, d19: the knots and the search
 * for p are FITPACK's, but the result is off by up to 9e-15, an arithmetic
 * not found), and a search for p that ends otherwise than by convergence.
 */
// LSEG: Adfin (22038) AdInterp IM:SPLREP (FITPACK curfit's algorithm and s = m - sqrt(2m), identified on the goldens)
function smoothingSpline(xs: number[], ys: number[]): Spline | undefined {
  const m = xs.length;
  const x = [0, ...xs];
  const y = [0, ...ys];
  const s = m - Math.sqrt(2 * m);
  const acc = TOL * s;
  const nmax = m + K1;
  const t = new Array<number>(nmax + 2).fill(0);
  const fpint = new Array<number>(nmax + 2).fill(0);
  const nrdata = new Array<number>(nmax + 2).fill(0);
  let n = NMIN;
  let [fpold, nplus, first] = [0, 0, true];
  let interpolationKnots = false;
  nrdata[1] = m - 2;
  let fp0 = 0;
  let a: number[][], z: number[], fp: number, q: number[][], c: number[], fpms: number;
  for (;;) {
    const nrint = n - NMIN + 1;
    const nk1 = n - K1;
    for (let j = 1, i = n; j <= K1; j++, i--) {
      t[j] = x[1];
      t[i] = x[m];
    }
    ({ a, z, fp, q } = leastSquares(x, y, t, n));
    if (n === NMIN) fp0 = fp;
    c = backSubstitute(a, z, nk1, K1);
    fpms = fp - s;
    if (Math.abs(fpms) < acc || (fpms < 0 && n === NMIN)) {
      // Accepted as it stands, but two or more fpknot knots are not matched (d18, d19), here too.
      return !interpolationKnots && n > NMIN + 1 ? undefined : { t: t.slice(0, n + 1), n, c };
    }
    if (fpms < 0) break;
    // The interpolation knots and fp still above s: a repeated X with very
    // different Y keeps the fit off its points. No golden: undefined, so
    // splrepMatched is false and EAV shows the not-available text.
    if (n === nmax) return undefined;
    // How many knots to add (FITPACK: one at first, then from the fall of fp).
    if (first) {
      nplus = 1;
      first = false;
    } else {
      let npl1 = nplus * 2;
      if (fpold - fp > acc) npl1 = Math.trunc((nplus * fpms) / (fpold - fp));
      nplus = Math.min(nplus * 2, Math.max(npl1, Math.trunc(nplus / 2), 1));
    }
    fpold = fp;
    // The sum of squared residuals of each knot interval (a point on a knot shared half and half).
    let [fpart, i, l, isNew] = [0, 1, K2, false];
    for (let it = 1; it <= m; it++) {
      if (!(x[it] < t[l] || l > nk1)) {
        isNew = true;
        l++;
      }
      let term = 0;
      let l0 = l - K2;
      for (let j = 1; j <= K1; j++) {
        l0++;
        term = term + c[l0] * q[it][j];
      }
      term = (term - y[it]) * (term - y[it]);
      fpart = fpart + term;
      if (!isNew) continue;
      const store = term * 0.5;
      fpint[i] = fpart - store;
      i++;
      fpart = store;
      isNew = false;
    }
    fpint[nrint] = fpart;
    for (let added = 0, nr = nrint; added < nplus; added++, nr++) {
      addKnot(x, t, n, fpint, nrdata, nr);
      n++;
      if (n === nmax) {
        // FITPACK's interpolation branch: the knots at x_3 .. x_{m-2}.
        for (let j = 0; j < m - K1; j++) t[K2 + j] = x[3 + j];
        interpolationKnots = true;
        break;
      }
    }
  }
  if (!interpolationKnots && n > NMIN + 1) return undefined; // two or more fpknot knots: not matched (d18, d19)
  // The smoothing parameter p, by curfit's iteration on f(p) = s.
  const nk1 = n - K1;
  const n8 = n - NMIN;
  const b = discontinuities(t, n);
  let [p1, f1, p3, f3] = [0, fp0 - s, -1, fpms];
  let p = 0;
  for (let i = 1; i <= nk1; i++) p = p + a[i][1];
  p = nk1 / p;
  let [ich1, ich3] = [0, 0];
  for (let iter = 1; iter <= MAXIT; iter++) {
    const pinv = 1 / p;
    const cc = z.slice();
    const g = a.map((row) => {
      const r = row.slice();
      r[K2] = 0;
      return r;
    });
    for (let it = 1; it <= n8; it++) {
      const h = new Array<number>(K2 + 2).fill(0);
      for (let i = 1; i <= K2; i++) h[i] = b[it][i] * pinv;
      let yi = 0;
      for (let j = it; j <= nk1; j++) {
        const [cos, sin, dd] = givens(h[1], g[j][1]);
        g[j][1] = dd;
        [yi, cc[j]] = rotate(cos, sin, yi, cc[j]);
        if (j === nk1) break;
        const i2 = j > n8 ? nk1 - j : K1;
        for (let i = 1; i <= i2; i++) {
          const i1 = i + 1;
          [h[i1], g[j][i1]] = rotate(cos, sin, h[i1], g[j][i1]);
          h[i] = h[i1];
        }
        h[i2 + 1] = 0;
      }
    }
    c = backSubstitute(g, cc, nk1, K2);
    fp = sumOfSquares(x, y, t, n, c, q);
    fpms = fp - s;
    if (Math.abs(fpms) < acc) return { t: t.slice(0, n + 1), n, c };
    if (iter === MAXIT) return undefined;
    const [p2, f2] = [p, fpms];
    if (ich3 === 0) {
      if (!(f2 - f3 > acc)) {
        [p3, f3] = [p2, f2];
        p = p * 0.04;
        if (p <= p1) p = p1 * 0.9 + p2 * 0.1;
        continue;
      }
      if (f2 < 0) ich3 = 1;
    }
    if (ich1 === 0) {
      if (!(f1 - f2 > acc)) {
        [p1, f1] = [p2, f2];
        p = p / 0.04;
        if (p3 < 0) continue;
        if (p >= p3) p = p2 * 0.1 + p3 * 0.9;
        continue;
      }
      if (f2 > 0) ich1 = 1;
    }
    if (f2 >= f1 || f2 <= f3) return undefined; // FITPACK's ier = 2: not proven
    ({ p, p1, f1, p3, f3 } = rationalStep(p1, f1, p2, f2, p3, f3));
  }
  return undefined;
}

/** The spline at x (FITPACK splev): the B-splines of the interval holding x, the end ones continued outside. */
function evaluate({ t, n, c }: Spline, x: number): number {
  const nk1 = n - K1;
  let l = K1;
  while (!(x < t[l + 1] || l === nk1)) l++;
  const h = bsplines(t, x, l);
  let sp = 0;
  let ll = l - K1;
  for (let j = 1; j <= K1; j++) {
    ll++;
    sp = sp + c[ll] * h[j];
  }
  return sp;
}

/** SPLREP at x. Only for curves splrepMatched accepts. */
// LSEG: Adfin (22038) AdInterp IM:SPLREP (behaviour and arithmetic from the goldens d1, d2b, d4, d6-d9, p4)
export function splrep(x: number, xs: number[], ys: number[]): number {
  return evaluate(smoothingSpline(xs, ys)!, x);
}

/**
 * The curves on which SPLREP is proven: those whose fit takes a path the
 * goldens prove (see smoothingSpline), on X values that span at most 15.
 * Wider X, and so every curve of date serials, is not ported: on date X
 * (45000..49383) the fit misses LSEG by 1 in the 15th decimal on 8 of 20 and
 * 12 of 20 points (goldens dates, d23), while the same Y on X 1..16 are exact
 * (d22, d1). So the X scale, not the Y values, decides it; the arithmetic
 * behind it is not found, and the largest X span proven exact is 15
 * (HANDOFF.md).
 */
// LSEG: Adfin (22038) AdInterp IM:SPLREP (the inputs the goldens prove; see docs/lseg-golden/HANDOFF.md)
export function splrepMatched(xs: number[], ys: number[]): boolean {
  return xs[xs.length - 1] - xs[0] <= 15 && smoothingSpline(xs, ys) !== undefined;
}
